import { useState } from 'react';
import type { EnrichmentSettings } from '../../../lib/enrichment/api';

interface Props {
  settings: EnrichmentSettings;
  onPatch: (updates: Partial<EnrichmentSettings>) => Promise<EnrichmentSettings | null>;
}

export function EnrichmentDimensionsTab({ settings, onPatch }: Props) {
  const [showMobileDisclaimer, setShowMobileDisclaimer] = useState(false);

  const tryEnableMobile = () => {
    if (!settings.mobile_phone_enabled) setShowMobileDisclaimer(true);
    else void onPatch({ mobile_phone_enabled: false });
  };

  const confirmMobile = async () => {
    await onPatch({ mobile_phone_enabled: true });
    setShowMobileDisclaimer(false);
  };

  return (
    <div className="text-[#37352f]">
      <p className="text-[13px] text-[#787774] mb-4">
        Scegli quali dimensioni del lead vuoi arricchire automaticamente. Ogni dimensione ha rischio
        GDPR e costo differenti.
      </p>

      <div className="divide-y divide-[rgba(55,53,47,0.09)] border border-[rgba(55,53,47,0.09)] rounded-md">
        <DimensionRow
          title="Firmografia azienda"
          description="P.IVA, ragione sociale, fatturato, ATECO, PEC. Fonte: registro pubblico (openapi.it + VIES)."
          cost="≈ 1 credito / lead"
          risk="basso"
          enabled={settings.firmografia_enabled}
          onToggle={() => onPatch({ firmografia_enabled: !settings.firmografia_enabled })}
        />
        <DimensionRow
          title="Persona B2B"
          description="Email professionale, nome verificato, ruolo, LinkedIn. Fonte: Dropcontact (EU, real-time, no DB)."
          cost="≈ 1 credito / lookup"
          risk="medio"
          enabled={settings.persona_enabled}
          onToggle={() => onPatch({ persona_enabled: !settings.persona_enabled })}
        />
        <DimensionRow
          title="Telefono mobile B2B"
          description="Numero mobile dell'interlocutore. Fonte: FullEnrich multi-source."
          cost="≈ 1-3 crediti / mobile valido"
          risk="alto"
          enabled={settings.mobile_phone_enabled}
          onToggle={tryEnableMobile}
          warning={
            !settings.mobile_phone_enabled
              ? "Richiede accettazione esplicita del rischio"
              : settings.mobile_phone_risk_accepted_at
                ? `Rischio accettato il ${new Date(settings.mobile_phone_risk_accepted_at).toLocaleDateString('it-IT')}`
                : undefined
          }
        />
      </div>

      {/* Base legale */}
      <div className="mt-6">
        <h3 className="text-[13px] font-medium mb-1">Base legale dichiarata</h3>
        <p className="text-[12px] text-[#787774] mb-3">
          Verrà mostrata nel campo provenance di ogni lead e usata negli audit GDPR.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {(
            [
              { v: 'legittimo_interesse', label: 'Legittimo interesse', sub: 'B2B contatti professionali, valutazione di necessità' },
              { v: 'consenso_esplicito', label: 'Consenso esplicito', sub: 'Spuntato dal lead sul form di raccolta' },
              { v: 'misto', label: 'Misto (per fonte)', sub: 'Diverso a seconda della sorgente del lead' },
            ] as const
          ).map((opt) => {
            const active = settings.base_legal === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => onPatch({ base_legal: opt.v })}
                className={`text-left px-3 py-2 rounded-md border transition-colors ${
                  active
                    ? 'border-[#37352f] bg-[rgba(55,53,47,0.04)]'
                    : 'border-[rgba(55,53,47,0.09)] hover:bg-[rgba(55,53,47,0.03)]'
                }`}
              >
                <div className="text-[13px] font-medium">{opt.label}</div>
                <div className="text-[12px] text-[#787774] mt-0.5">{opt.sub}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Disclaimer modale mobile */}
      {showMobileDisclaimer && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-md max-w-md w-full p-5 text-[#37352f]">
            <h3 className="text-[15px] font-semibold mb-2">Attivazione telefono mobile B2B</h3>
            <p className="text-[13px] text-[#787774] mb-3">
              Il telefono mobile è il dato personale a più alto rischio nel trattamento B2B.
              Attivando questa funzione:
            </p>
            <ul className="text-[13px] text-[#37352f] list-disc pl-5 space-y-1 mb-4">
              <li>I provider usano database di terzi (rischio GDPR superiore)</li>
              <li>Devi documentare la base legale del trattamento</li>
              <li>Ogni lead può richiedere la rimozione del dato</li>
              <li>L'attivazione viene loggata nel registro audit</li>
            </ul>
            <div className="flex justify-end gap-2 pt-3 border-t border-[rgba(55,53,47,0.09)]">
              <button
                onClick={() => setShowMobileDisclaimer(false)}
                className="px-3 py-1.5 text-[13px] text-[#37352f] hover:bg-[rgba(55,53,47,0.06)] rounded"
              >
                Annulla
              </button>
              <button
                onClick={confirmMobile}
                className="px-3 py-1.5 text-[13px] text-white bg-[#e03e3e] hover:bg-[#c0382f] rounded font-medium"
              >
                Accetto il rischio e attivo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DimensionRowProps {
  title: string;
  description: string;
  cost: string;
  risk: 'basso' | 'medio' | 'alto';
  enabled: boolean;
  onToggle: () => void;
  warning?: string;
}
function DimensionRow({ title, description, cost, risk, enabled, onToggle, warning }: DimensionRowProps) {
  const riskColor =
    risk === 'basso' ? 'text-[#0f7b6c]' : risk === 'medio' ? 'text-[#cb912f]' : 'text-[#e03e3e]';
  return (
    <div className="flex items-start gap-3 px-3 py-3">
      <button
        onClick={onToggle}
        className={`mt-0.5 relative inline-flex w-9 h-5 rounded-full transition-colors shrink-0 ${
          enabled ? 'bg-[#37352f]' : 'bg-[rgba(55,53,47,0.16)]'
        }`}
        aria-label="toggle"
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-4' : ''
          }`}
        />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[13px] font-medium">{title}</span>
          <span className={`text-[11px] ${riskColor}`}>· rischio {risk}</span>
          <span className="text-[11px] text-[#9b9a97]">· {cost}</span>
        </div>
        <p className="text-[12px] text-[#787774] mt-0.5">{description}</p>
        {warning && <p className="text-[11px] text-[#cb912f] mt-1">{warning}</p>}
      </div>
    </div>
  );
}
